import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { useModalController } from '@uikit';

import { useLoginByTelegramMutation } from '@query';

import { TelegramUser } from '@api';

import TelegramLoginButton from '@shared/common/components/TelegramButton/TelegramButton';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { useTagManager } from '@shared/common/providers/GoogleTagManagerProvider';
import { namedErrors } from '@shared/constants';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import { useRegisterQueryParams } from '@shared/hooks/useRegisterQueryParams';

import { SocialAuthButtonProps } from '../AuthModal';
import CreateNewAccountModal from '../CreateNewAccountModal';

const TelegramAuthButton = ({ className, disabled, children }: SocialAuthButtonProps) => {
  const { mutateAsync: telegramLogin } = useLoginByTelegramMutation();
  const loginModalController = useAuthModal();
  const createAccountModal = useModalController();
  const addQueryRegister = useRegisterQueryParams();
  const [user, setUser] = useState<TelegramUser | undefined>();
  const { triggerAuthEvent } = useTagManager();

  const isDisabled = useMemo(() => {
    return disabled;
  }, [disabled]);

  const loginTelegram = async (
    userCreationAllowed: boolean,
    userData: TelegramUser | undefined = user,
  ) => {
    try {
      if (!userData) {
        toast.error(namedErrors.DEFAULT_RETRY);

        return;
      }

      const data = await telegramLogin({ userCreationAllowed, userData });

      if (data) {
        triggerAuthEvent(data.id, {
          isNewUser: userCreationAllowed,
        });

        if (data.isNewUser) {
          addQueryRegister({
            loginMethod: 'telegram',
          });
        }

        loginModalController.close();
        createAccountModal.close();
      }
    } catch (error) {
      if (!createAccountModal.isOpen) {
        createAccountModal.open();
      } else {
        displayErrorToast({ error });
      }
    }
  };

  const handleAuth = (userData: TelegramUser) => {
    setUser(userData);
    loginTelegram(false, userData);
  };

  return (
    <>
      <TelegramLoginButton asChild disabled={isDisabled} className={className} onAuth={handleAuth}>
        {children}
      </TelegramLoginButton>
      <CreateNewAccountModal
        modalProps={createAccountModal.props}
        onConfirm={loginTelegram}
        onClose={createAccountModal.close}
      />
    </>
  );
};

export default TelegramAuthButton;
